import type * as CSS from 'csstype';
import {EnrichedAstNode} from '../../../types';

interface PercentageToPixelsArgs {
    percentage: number;
    styles: CSS.StandardLonghandProperties<string | number>;
    parentWidth: number;
    parentHeight: number;
    parentFontSizePx: number;
    fontSizePx: number;
    node: EnrichedAstNode;
}

const getPercentageOfParentWidthInPixels = ({percentage, parentWidth}: PercentageToPixelsArgs) =>
    (percentage / 100) * Math.abs(parentWidth);
const getPercentageOfParentHeightInPixels = ({percentage, parentHeight}: PercentageToPixelsArgs) =>
    (percentage / 100) * Math.abs(parentHeight);
const getPercentageOfSelfWidthInPixels = ({percentage, node}: PercentageToPixelsArgs) =>
    (percentage / 100) * Math.abs(node.position.width);
const getPercentageOfSelfHeightInPixels = ({percentage, node}: PercentageToPixelsArgs) =>
    (percentage / 100) * Math.abs(node.position.height);

export const PERCENTAGE_TO_PIXELS_CONVERSION_FUNCTIONS: Record<string, (args: PercentageToPixelsArgs) => number> = {
    width: getPercentageOfParentWidthInPixels,
    minWidth: getPercentageOfParentWidthInPixels,
    maxWidth: getPercentageOfParentWidthInPixels,
    height: getPercentageOfParentHeightInPixels,
    minHeight: getPercentageOfParentHeightInPixels,
    maxHeight: getPercentageOfParentHeightInPixels,

    paddingTop: getPercentageOfParentWidthInPixels,
    paddingRight: getPercentageOfParentWidthInPixels,
    paddingBottom: getPercentageOfParentWidthInPixels,
    paddingLeft: getPercentageOfParentWidthInPixels,

    gap: ({percentage, node, styles}) => {
        const flexDirection = (styles['flexDirection'] || 'row') as string;
        const parentDirectionalSize = flexDirection.includes('col') ? node.position.height : node.position.width;
        return (percentage / 100) * parentDirectionalSize;
    },

    fontSize: ({percentage, parentFontSizePx}) => (percentage / 100) * parentFontSizePx,
    lineHeight: ({percentage, fontSizePx}) => (percentage / 100) * fontSizePx,

    top: getPercentageOfParentHeightInPixels,
    right: getPercentageOfParentWidthInPixels,
    bottom: getPercentageOfParentHeightInPixels,
    left: getPercentageOfParentWidthInPixels,

    marginTop: getPercentageOfParentWidthInPixels,
    marginRight: getPercentageOfParentWidthInPixels,
    marginBottom: getPercentageOfParentWidthInPixels,
    marginLeft: getPercentageOfParentWidthInPixels,

    columnGap: getPercentageOfSelfWidthInPixels,
    rowGap: getPercentageOfSelfHeightInPixels,
};

interface PixelToPercentageArgs {
    valueInPx: number;
    styles: CSS.StandardLonghandProperties<string | number>;
    parentWidth: number;
    parentHeight: number;
    parentFontSizePx: number;
    fontSizePx: number;
    node: EnrichedAstNode;
}

const convertPxValueToPercentageOfParentWidth = ({valueInPx, parentWidth}: PixelToPercentageArgs) =>
    (valueInPx / Math.abs(parentWidth)) * 100;

const convertPxValueToPercentageOfParentHeight = ({valueInPx, parentHeight}: PixelToPercentageArgs) =>
    (valueInPx / Math.abs(parentHeight)) * 100;

const convertPxValueToPercentageOfSelfHeight = ({valueInPx, node}: PixelToPercentageArgs) =>
    (valueInPx / Math.abs(node.position.height)) * 100;
const convertPxValueToPercentageOfSelfWidth = ({valueInPx, node}: PixelToPercentageArgs) =>
    (valueInPx / Math.abs(node.position.width)) * 100;

export const PIXEL_TO_PERCENTAGE_CONVERSION_FUNCTIONS: Record<string, (args: PixelToPercentageArgs) => number> = {
    width: convertPxValueToPercentageOfParentWidth,
    minWidth: convertPxValueToPercentageOfParentWidth,
    maxWidth: convertPxValueToPercentageOfParentWidth,
    height: convertPxValueToPercentageOfParentHeight,
    minHeight: convertPxValueToPercentageOfParentHeight,
    maxHeight: convertPxValueToPercentageOfParentHeight,

    paddingTop: convertPxValueToPercentageOfParentWidth,
    paddingRight: convertPxValueToPercentageOfParentWidth,
    paddingBottom: convertPxValueToPercentageOfParentWidth,
    paddingLeft: convertPxValueToPercentageOfParentWidth,

    gap: ({valueInPx, node, styles}) => {
        const flexDirection = (styles['flexDirection'] || 'row') as string;
        const directionalSize = flexDirection.includes('col') ? node.position.height : node.position.width;
        return (valueInPx / directionalSize) * 100;
    },

    fontSize: ({valueInPx, parentFontSizePx}) => (valueInPx / parentFontSizePx) * 100,
    lineHeight: ({valueInPx, fontSizePx}) => (valueInPx / fontSizePx) * 100,

    top: convertPxValueToPercentageOfParentHeight,
    right: convertPxValueToPercentageOfParentWidth,
    bottom: convertPxValueToPercentageOfParentHeight,
    left: convertPxValueToPercentageOfParentWidth,

    marginTop: convertPxValueToPercentageOfParentWidth,
    marginRight: convertPxValueToPercentageOfParentWidth,
    marginBottom: convertPxValueToPercentageOfParentWidth,
    marginLeft: convertPxValueToPercentageOfParentWidth,

    columnGap: convertPxValueToPercentageOfSelfWidth,
    rowGap: convertPxValueToPercentageOfSelfHeight,
};
